import { useCallback, useEffect, useMemo, useState } from 'react';
import { getProductById } from '../../catalog/infra/productsRepo'
import { listActiveBrandsLocal } from '../../catalog/infra/brandsRepoRead'
import { listCategories } from '../../catalog/infra/categoriesRepoRead'
import { listVariantsForProduct } from '../../catalog/infra/productVariantsRepo';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { showError, showInfo, showSuccess } from '../../shared/ui/appToast';
import type { CategoryOption } from '../../catalog/domain-api';
import { toLocalStartOfDayIso, toLocalEndOfDayIso } from '@shared/vueltoDateTime';
import {
  createPromotionIntake,
  getPromotionByIdCache,
  updatePromotionIntake,
} from '../infra/promotionsRepo';
import type {
  PromotionApplyMode,
  PromotionChannel,
  PromotionConditions,
  PromotionKind,
  PromotionScope,
  PromotionTargetType,
  PromotionUpsertInput,
  Weekday,
} from '../domain/promotionTypes';
import { DEFAULT_PROMOTION_CHANNELS } from '../domain/promotionTypes';
import { validatePromotionInput } from './validatePromotionInput';
import { getCatalogRamSnapshotCache } from '../../catalog/infra/catalogRamSnapshotCache';
import { formatMoney } from '../../shared/format/public-api';

export type PromotionEditTargetDraft = {
  id?: string;
  targetType: PromotionTargetType;
  targetId: string;
  label: string;
};

const DEFAULT_KIND: PromotionKind = 'percent_off';
const DEFAULT_SCOPE: PromotionScope = 'line';
const DEFAULT_APPLY_MODE: PromotionApplyMode = 'automatic';

const parseOptionalNumber = (raw: string): number | null => {
  const t = raw.trim();
  if (!t) return null;
  const n = Number(t.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const parseOptionalInt = (raw: string): number | null => {
  const n = parseOptionalNumber(raw);
  return n == null ? null : Math.trunc(n);
};

const targetKey = (targetType: PromotionTargetType, targetId: string) => `${targetType}:${targetId}`;

export function usePromotionEditScreen({
  promotionId,
  duplicateFromId = null,
  createTemplateId = null,
  onClose,
}: {
  promotionId: string | null;
  duplicateFromId?: string | null;
  createTemplateId?: string | null;
  onClose: () => void;
}) {
  const isEdit = Boolean(promotionId);
  const isDuplicateDraft = !promotionId && Boolean(duplicateFromId);
  const sourceId = promotionId ?? duplicateFromId ?? null;
  const { canEdit } = useBranchPermission('Promotions');
  const branches = useSessionStore((s) => s.branches);
  const currentBranchId = useSessionStore((s) => s.currentBranchId);

  const [loading, setLoading] = useState(Boolean(sourceId));
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState<PromotionKind>(DEFAULT_KIND);
  const [scope, setScope] = useState<PromotionScope>(DEFAULT_SCOPE);
  const [valuePercent, setValuePercent] = useState('');
  const [valueFixed, setValueFixed] = useState('');
  const [comboBuyQty, setComboBuyQty] = useState('');
  const [comboPayQty, setComboPayQty] = useState('');
  const [comboBundlePrice, setComboBundlePrice] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [applyMode, setApplyMode] = useState<PromotionApplyMode>(DEFAULT_APPLY_MODE);
  const [promoCode, setPromoCode] = useState('');
  const [priority, setPriority] = useState('0');
  const [stackable, setStackable] = useState(false);
  const [channelPos, setChannelPos] = useState(true);
  const [channelOnline, setChannelOnline] = useState(true);
  const [targets, setTargets] = useState<PromotionEditTargetDraft[]>([]);
  const [selectedBranchIds, setSelectedBranchIds] = useState<string[]>([]);

  const [minPurchaseAmount, setMinPurchaseAmount] = useState('');
  const [minQuantity, setMinQuantity] = useState('');
  const [usageLimit, setUsageLimit] = useState('');
  const [selectedWeekdays, setSelectedWeekdays] = useState<Weekday[]>([]);
  const [scheduleStartTime, setScheduleStartTime] = useState('');
  const [scheduleEndTime, setScheduleEndTime] = useState('');

  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
  const [brandOptions, setBrandOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [mastersLoading, setMastersLoading] = useState(true);

  const clearValidation = useCallback(() => {
    setValidationMessage(null);
  }, []);

  const wrapSetter =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      clearValidation();
      setter(value);
    };

  useEffect(() => {
    if (!canEdit && !loading) {
      showInfo('Solo lectura', 'No podés editar promos en este local.');
    }
  }, [canEdit, loading]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [categories, brands] = await Promise.all([
          listCategories({ includeInactive: false }),
          listActiveBrandsLocal(),
        ]);
        if (cancelled) return;
        setCategoryOptions(
          categories.map((c) => ({
            id: c.id,
            label: c.name,
            description: c.description,
            color: c.color,
            appliesTo: c.appliesTo,
          }))
        );
        setBrandOptions(brands);
      } catch (e) {
        console.warn('[usePromotionEditScreen] masters load', e);
      } finally {
        if (!cancelled) setMastersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const resolveTargetLabel = useCallback(
    async (targetType: PromotionTargetType, targetId: string): Promise<string> => {
      if (targetType === 'category') {
        return categoryOptions.find((c) => c.id === targetId)?.label ?? targetId;
      }
      if (targetType === 'brand') {
        return brandOptions.find((b) => b.id === targetId)?.name ?? targetId;
      }
      if (targetType === 'variant') {
        // #793: Resolver variante desde cache RAM en vez de db stub (que devuelve undefined).
        try {
          const cached = getCatalogRamSnapshotCache()?.productVariants.find((v) => String(v.id) === targetId);
          if (cached) {
            const suffix = String((cached as { name_suffix?: string | null }).name_suffix ?? '').trim() || null;
            let variantPart = suffix;
            if (!variantPart) {
              const attrs = (cached as { attributes?: Record<string, unknown> }).attributes;
              if (attrs && typeof attrs === 'object') {
                const values = Object.values(attrs).filter((v) => typeof v === 'string' && v.trim());
                if (values.length > 0) variantPart = values.join(' · ');
              }
            }
            const productId = String((cached as { product_id?: string }).product_id ?? '');
            const product = await getProductById(productId);
            const productName = product && typeof product === 'object' && 'name' in product ? String((product as { name?: string }).name ?? '') : '';
            return variantPart ? `${productName} (${variantPart})` : productName || targetId;
          }
        } catch {
          /* fallback */
        }
        return targetId;
      }
      try {
        const product = await getProductById(targetId);
        if (product && typeof product === 'object' && 'name' in product) {
          return String((product as { name?: string }).name ?? targetId);
        }
      } catch {
        /* fallback */
      }
      return targetId;
    },
    [brandOptions, categoryOptions]
  );

  useEffect(() => {
    if (mastersLoading || targets.length === 0) return;
    setTargets((prev) =>
      prev.map((t) => {
        if (t.targetType === 'category') {
          return {
            ...t,
            label: categoryOptions.find((c) => c.id === t.targetId)?.label ?? t.label,
          };
        }
        if (t.targetType === 'brand') {
          return {
            ...t,
            label: brandOptions.find((b) => b.id === t.targetId)?.name ?? t.label,
          };
        }
        return t;
      })
    );
  }, [brandOptions, categoryOptions, mastersLoading, targets.length]);

  const load = useCallback(async () => {
    if (!sourceId) {
      if (createTemplateId === 'bogo_2x1') {
        setName('2×1 ejemplo');
        setDescription('Promo de ejemplo para probar en el mostrador.');
        setKind('combo_buy_x_get_y');
        setScope('line');
        setComboBuyQty('2');
        setComboPayQty('1');
        setValuePercent('');
        setValueFixed('');
        setIsActive(false);
        setApplyMode('automatic');
        if (currentBranchId) {
          setSelectedBranchIds([currentBranchId]);
        }
      }
      setLoading(false);
      return;
    }
    setLoadError(null);
    try {
      const promo = await getPromotionByIdCache(sourceId);
      if (!promo) {
        setLoadError('Promo no encontrada.');
        return;
      }
      setName(isDuplicateDraft ? `${promo.name} (copia)` : promo.name);
      setDescription(promo.description ?? '');
      setKind(promo.kind);
      setScope(promo.scope);
      setValuePercent(promo.valuePercent != null ? String(promo.valuePercent) : '');
      setValueFixed(promo.valueFixed != null ? String(promo.valueFixed) : '');
      setComboBuyQty(promo.comboBuyQty != null ? String(promo.comboBuyQty) : '');
      setComboPayQty(promo.comboPayQty != null ? String(promo.comboPayQty) : '');
      setComboBundlePrice(promo.comboBundlePrice != null ? String(promo.comboBundlePrice) : '');
      setStartsAt(promo.startsAt?.slice(0, 10) ?? '');
      setEndsAt(promo.endsAt?.slice(0, 10) ?? '');
      setIsActive(isDuplicateDraft ? false : promo.isActive);
      setApplyMode(promo.applyMode);
      setPromoCode(promo.promoCode ?? '');
      setPriority(String(promo.priority ?? 0));
      setStackable(promo.stackable);
      {
        const channels =
          promo.channels?.length ? promo.channels : [...DEFAULT_PROMOTION_CHANNELS];
        setChannelPos(channels.includes('pos'));
        setChannelOnline(channels.includes('online'));
      }
      setSelectedBranchIds(promo.branchIds ?? []);
      setMinPurchaseAmount(promo.conditions?.minPurchaseAmount != null ? String(promo.conditions.minPurchaseAmount) : '');
      setMinQuantity(promo.conditions?.minQuantity != null ? String(promo.conditions.minQuantity) : '');
      setUsageLimit(promo.conditions?.usageLimit != null ? String(promo.conditions.usageLimit) : '');
      setSelectedWeekdays(promo.conditions?.weekdays ?? []);
      setScheduleStartTime(promo.conditions?.scheduleWindow?.startTime ?? '');
      setScheduleEndTime(promo.conditions?.scheduleWindow?.endTime ?? '');
      const labeledTargets = await Promise.all(
        promo.targets.map(async (t) => ({
          ...(isDuplicateDraft ? {} : { id: t.id }),
          targetType: t.targetType,
          targetId: t.targetId,
          label: await resolveTargetLabel(t.targetType, t.targetId),
        }))
      );
      setTargets(labeledTargets);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message?: string }).message)
          : 'No se pudo cargar la promoción.';
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  }, [sourceId, isDuplicateDraft, resolveTargetLabel, createTemplateId, currentBranchId]);

  useEffect(() => {
    void load();
  }, [load]);

  const addTarget = useCallback(
    (draft: Omit<PromotionEditTargetDraft, 'label'> & { label?: string }) => {
      clearValidation();
      setTargets((prev) => {
        const key = targetKey(draft.targetType, draft.targetId);
        if (prev.some((t) => targetKey(t.targetType, t.targetId) === key)) {
          return prev;
        }
        const label =
          draft.label ??
          (draft.targetType === 'category'
            ? categoryOptions.find((c) => c.id === draft.targetId)?.label
            : draft.targetType === 'brand'
              ? brandOptions.find((b) => b.id === draft.targetId)?.name
              : draft.targetId) ??
          draft.targetId;
        return [...prev, { id: draft.id, targetType: draft.targetType, targetId: draft.targetId, label }];
      });
    },
    [brandOptions, categoryOptions, clearValidation]
  );

  const removeTarget = useCallback(
    (targetType: PromotionTargetType, targetId: string) => {
      clearValidation();
      setTargets((prev) =>
        prev.filter((t) => !(t.targetType === targetType && t.targetId === targetId))
      );
    },
    [clearValidation]
  );

  const clearTargets = useCallback(() => {
    clearValidation();
    setTargets([]);
  }, [clearValidation]);

  const toggleBranchId = useCallback(
    (branchId: string) => {
      clearValidation();
      setSelectedBranchIds((prev) =>
        prev.includes(branchId) ? prev.filter((id) => id !== branchId) : [...prev, branchId]
      );
    },
    [clearValidation]
  );

  const clearSelectedBranches = useCallback(() => {
    clearValidation();
    setSelectedBranchIds([]);
  }, [clearValidation]);

  const toggleWeekday = useCallback(
    (day: Weekday) => {
      clearValidation();
      setSelectedWeekdays((prev) =>
        prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
      );
    },
    [clearValidation]
  );

  const toggleChannel = useCallback(
    (channel: PromotionChannel, enabled: boolean) => {
      clearValidation();
      if (channel === 'pos') setChannelPos(enabled);
      else setChannelOnline(enabled);
    },
    [clearValidation]
  );

  const buildConditions = useCallback((): PromotionConditions | null => {
    const minAmount = parseOptionalNumber(minPurchaseAmount);
    const minQty = parseOptionalInt(minQuantity);
    const limit = parseOptionalInt(usageLimit);
    // Schedule window requires both start and end. Partial (one field only) is
    // rejected by validatePromotionInput, but buildConditions is defensive: if
    // only one is set, drop the window entirely rather than persist a half-window.
    const hasSchedule = Boolean(scheduleStartTime.trim()) && Boolean(scheduleEndTime.trim());
    const hasAny =
      minAmount != null ||
      minQty != null ||
      limit != null ||
      selectedWeekdays.length > 0 ||
      hasSchedule;
    if (!hasAny) return null;
    return {
      minPurchaseAmount: minAmount,
      minQuantity: minQty,
      usageLimit: limit,
      weekdays: selectedWeekdays.length > 0 ? selectedWeekdays : null,
      scheduleWindow: hasSchedule
        ? { startTime: scheduleStartTime.trim(), endTime: scheduleEndTime.trim() }
        : null,
    };
  }, [minPurchaseAmount, minQuantity, usageLimit, selectedWeekdays, scheduleStartTime, scheduleEndTime]);

  const buildInput = useCallback((): PromotionUpsertInput | null => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      const msg = 'Poné un nombre a la promo.';
      setValidationMessage(msg);
      showError('Nombre requerido', msg);
      return null;
    }

    const channels: PromotionChannel[] = [];
    if (channelPos) channels.push('pos');
    if (channelOnline) channels.push('online');
    if (channels.length === 0) {
      const msg = 'Elegí POS, online, o los dos.';
      setValidationMessage(msg);
      showError('Canal requerido', msg);
      return null;
    }

    const input: PromotionUpsertInput = {
      name: trimmedName,
      description: description.trim() || null,
      kind,
      scope,
      valuePercent: parseOptionalNumber(valuePercent),
      valueFixed: parseOptionalNumber(valueFixed),
      comboBuyQty: parseOptionalInt(comboBuyQty),
      comboPayQty: parseOptionalInt(comboPayQty),
      comboBundlePrice: parseOptionalNumber(comboBundlePrice),
      startsAt: startsAt.trim() ? toLocalStartOfDayIso(startsAt) : null,
      endsAt: endsAt.trim() ? toLocalEndOfDayIso(endsAt) : null,
      isActive,
      applyMode,
      promoCode: promoCode.trim() || null,
      priority: parseOptionalInt(priority) ?? 0,
      stackable,
      channels,
      conditions: buildConditions(),
      branchIds: selectedBranchIds.length > 0 ? selectedBranchIds : null,
      targets: targets.map((t) =>
        t.id
          ? { id: t.id, targetType: t.targetType, targetId: t.targetId }
          : { targetType: t.targetType, targetId: t.targetId }
      ),
    };

    const error = validatePromotionInput(input, { kind, applyMode, scheduleStartTime, scheduleEndTime });
    if (error) {
      setValidationMessage(error.message);
      showError(error.title, error.message);
      return null;
    }

    setValidationMessage(null);
    return input;
  }, [
    applyMode,
    channelOnline,
    channelPos,
    comboBundlePrice,
    comboBuyQty,
    comboPayQty,
    description,
    endsAt,
    isActive,
    kind,
    name,
    priority,
    promoCode,
    scope,
    selectedBranchIds,
    stackable,
    startsAt,
    targets,
    valueFixed,
    valuePercent,
    minPurchaseAmount,
    minQuantity,
    usageLimit,
    selectedWeekdays,
    scheduleStartTime,
    scheduleEndTime,
  ]);

  const onSave = useCallback(async () => {
    if (!canEdit) return;
    const input = buildInput();
    if (!input) return;

    setSaving(true);
    try {
      if (isEdit && promotionId) {
        await updatePromotionIntake(promotionId, input);
        showSuccess('Promoción guardada');
      } else {
        await createPromotionIntake(input);
        showSuccess('Promoción creada');
      }
      onClose();
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message?: string }).message)
          : 'No se pudo guardar la promo.';
      setValidationMessage(msg);
      showError('Error al guardar', msg);
    } finally {
      setSaving(false);
    }
  }, [buildInput, canEdit, isEdit, onClose, promotionId]);

  const screenTitle = useMemo(() => {
    if (isEdit) return 'Editar promoción';
    if (isDuplicateDraft) return 'Duplicar promoción';
    return 'Nueva promoción';
  }, [isEdit, isDuplicateDraft]);

  const brandPickerOptions = useMemo(
    () => brandOptions.map((b) => ({ label: b.name, value: b.id })),
    [brandOptions]
  );

  // Audit #3: "N por $X" igual o más caro que la suma suelta → promo muerta.
  const comboBundleWarning = useMemo(() => {
    if (kind !== 'combo_n_for_price') return null;
    const bundle = parseOptionalNumber(comboBundlePrice);
    const qty = parseOptionalInt(comboBuyQty);
    if (bundle == null || qty == null || qty <= 0) return null;
    const productTargets = targets.filter((t) => t.targetType === 'product');
    if (productTargets.length === 0) return null;
    const cache = getCatalogRamSnapshotCache();
    if (!cache) return null;
    const prices = productTargets.map(
      (t) => cache.products.find((prod) => prod.id === t.targetId)?.price,
    );
    if (prices.some((p) => typeof p !== 'number')) return null;
    const minSum = Math.min(...(prices as number[])) * qty;
    if (bundle < minSum) return null;
    return `El pack sale igual o más caro que ${qty} × el más barato (${formatMoney(minSum)}). La promo no se va a aplicar.`;
  }, [kind, comboBundlePrice, comboBuyQty, targets]);

  return {
    isEdit,
    isDuplicateDraft,
    promotionId,
    canEdit,
    loading,
    saving,
    loadError,
    validationMessage,
    screenTitle,
    currentBranchId,
    branches,
    categoryOptions,
    brandPickerOptions,
    mastersLoading,
    name,
    setName: wrapSetter(setName),
    description,
    setDescription: wrapSetter(setDescription),
    kind,
    setKind: wrapSetter(setKind),
    scope,
    setScope: wrapSetter(setScope),
    valuePercent,
    setValuePercent: wrapSetter(setValuePercent),
    valueFixed,
    setValueFixed: wrapSetter(setValueFixed),
    comboBuyQty,
    setComboBuyQty: wrapSetter(setComboBuyQty),
    comboPayQty,
    setComboPayQty: wrapSetter(setComboPayQty),
    comboBundlePrice,
    setComboBundlePrice: wrapSetter(setComboBundlePrice),
    comboBundleWarning,
    startsAt,
    setStartsAt: wrapSetter(setStartsAt),
    endsAt,
    setEndsAt: wrapSetter(setEndsAt),
    isActive,
    setIsActive: wrapSetter(setIsActive),
    applyMode,
    setApplyMode: wrapSetter(setApplyMode),
    promoCode,
    setPromoCode: wrapSetter(setPromoCode),
    priority,
    setPriority: wrapSetter(setPriority),
    stackable,
    setStackable: wrapSetter(setStackable),
    channelPos,
    channelOnline,
    toggleChannel,
    targets,
    addTarget,
    removeTarget,
    clearTargets,
    selectedBranchIds,
    toggleBranchId,
    clearSelectedBranches,
    minPurchaseAmount,
    setMinPurchaseAmount: wrapSetter(setMinPurchaseAmount),
    minQuantity,
    setMinQuantity: wrapSetter(setMinQuantity),
    usageLimit,
    setUsageLimit: wrapSetter(setUsageLimit),
    selectedWeekdays,
    toggleWeekday,
    scheduleStartTime,
    setScheduleStartTime: wrapSetter(setScheduleStartTime),
    scheduleEndTime,
    setScheduleEndTime: wrapSetter(setScheduleEndTime),
    onSave,
  };
}

export type PromotionEditScreenViewModel = ReturnType<typeof usePromotionEditScreen>;
